/**
 * ARMAR EL RECIBO EN EL DISPOSITIVO, sin preguntarle al servidor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  POR QUÉ HACE FALTA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Cuando se vende sin internet, el cliente **igual espera su recibo**. No se le
 * puede decir "te lo doy cuando vuelva la señal".
 *
 * Así que se construye aquí, con la misma forma que devuelve el servidor, para
 * que la pantalla del recibo y la impresión térmica funcionen exactamente igual
 * en los dos casos. La pantalla de recibo no sabe (ni le importa) si la venta
 * pasó por el servidor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  EL NÚMERO PROVISIONAL
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * El número de recibo definitivo **solo lo puede dar el servidor**: es un
 * consecutivo del negocio, y dos celulares vendiendo a la vez sin conexión no
 * pueden repartírselo sin chocar.
 *
 * Por eso el ticket sin conexión sale con una referencia provisional tipo
 * `OFF-4F2A`, y al sincronizar la venta recibe su número real. La referencia
 * provisional se guarda para poder relacionar el papel con la venta después.
 */

import type { PagoIn, Sale, SaleItem, SalePayment, TenantCurrency } from "@/lib/types";
import { toNumber } from "@/lib/money";
import { desgloseDeLinea, type LineaCarrito, type TotalesCarrito } from "@/stores/carrito";

/** Redondea a 4 decimales, igual que el backend en cada paso. */
function q(n: number): string {
  return (Math.round(n * 10000) / 10000).toFixed(4);
}

/** Referencia provisional legible: OFF-4F2A */
export function referenciaProvisional(clientUuid: string): string {
  return `OFF-${clientUuid.replace(/-/g, "").slice(0, 4).toUpperCase()}`;
}

export function construirReciboLocal(opciones: {
  clientUuid: string;
  lineas: LineaCarrito[];
  totales: TotalesCarrito;
  pagos: PagoIn[];
  monedas: TenantCurrency[];
  metodos: { id: string; name: string; type: string }[];
  monedaPrincipal: string;
  vendedor: { id: string | null; nombre: string | null };
  clienteId: string | null;
  clienteNombre: string | null;
  nota: string | null;
  /** El código de descuento que se aplicó, si se aplicó alguno. */
  codigoDescuento?: string | null;
  /**
   * Con qué lista se cobró. Sin ella, cada línea del papel salía al precio
   * normal mientras el total iba con la lista: el recibo no sumaba.
   */
  listaPreciosId?: string | null;
  /** Los de la sucursal donde se vende, para el recibo. Ver `Sale.telefono_sucursal`. */
  telefonoSucursal?: string | null;
  direccionSucursal?: string | null;
}): Sale {
  const {
    clientUuid,
    lineas,
    totales,
    pagos,
    monedas,
    metodos,
    monedaPrincipal,
    vendedor,
    clienteId,
    clienteNombre,
    nota,
    codigoDescuento,
    listaPreciosId = null,
  } = opciones;

  const ahora = new Date().toISOString();
  const tasaDe = (codigo: string): number => {
    if (codigo === monedaPrincipal) return 1;
    return toNumber(monedas.find((m) => m.currency_code === codigo)?.current_rate);
  };

  // --- Líneas ---
  const items: SaleItem[] = lineas.map((l, indice) => {
    // La MISMA cuenta que la pantalla y el total, con la lista de la venta.
    const { precio, base, impuesto, total: lineaTotal } = desgloseDeLinea(l, listaPreciosId);
    const tasa = toNumber(l.tasaImpuesto);

    return {
      // Id local: el definitivo lo asigna el servidor al sincronizar.
      id: `local-${clientUuid}-${indice}`,
      item_id: l.itemId,
      variant_id: l.variantId,
      kind: l.kind,
      name: l.nombre,
      variant_name: l.varianteNombre,
      qty: String(l.cantidad),
      unit_price: q(precio),
      // De cuánto era la recarga. El servidor lo devuelve igual al sincronizar.
      monto_base: l.monto != null ? q(l.monto) : null,
      discount: q(l.descuento),
      tax_name: l.impuestoNombre,
      tax_rate: q(tasa),
      tax_inclusive: l.impuestoIncluido,
      tax_amount: q(impuesto),
      line_subtotal: q(base),
      line_total: q(lineaTotal),
      exento_de: l.exentoDe ?? [],
      staff_user_id: l.staffUserId,
      staff_name: l.staffNombre,
      // La comisión la calcula el servidor (depende de la configuración de la
      // persona, que el dispositivo no necesita conocer). En el ticket del
      // cliente no sale, así que no se pierde nada.
      commission_amount: "0.0000",
    };
  });

  // --- Pagos ---
  let recibido = 0;
  let vuelto = 0;

  const pagosSalida: SalePayment[] = pagos.map((p, indice) => {
    const tasa = tasaDe(p.currency_code);
    const monto = toNumber(p.amount);
    const enPrincipal = tasa > 0 ? monto / tasa : 0;

    if (p.direction === "change") vuelto += enPrincipal;
    else recibido += enPrincipal;

    const metodo = metodos.find((m) => m.id === p.payment_method_id);

    return {
      id: `local-pago-${clientUuid}-${indice}`,
      direction: p.direction ?? "in",
      payment_method_id: p.payment_method_id,
      method_name: metodo?.name ?? "Pago",
      method_type: metodo?.type ?? "other",
      currency_code: p.currency_code,
      amount: q(monto),
      exchange_rate: (Math.round(tasa * 1e10) / 1e10).toFixed(10),
      amount_in_primary: q(enPrincipal),
      reference: p.reference ?? null,
      datos: p.datos ?? null,
    };
  });

  const ajuste = recibido - vuelto - totales.total;

  return {
    // Id local: el servidor asignará el suyo. Con el `client_uuid` podremos
    // relacionarlos cuando sincronice.
    id: clientUuid,
    number: referenciaProvisional(clientUuid),
    status: "completed",
    currency_code: monedaPrincipal,

    subtotal: q(totales.subtotal),
    discount_total: q(totales.descuentoTotal),
    /*
      ⚠️ El código también viaja al recibo de sin conexión.

      Sin esto, una venta cobrada sin internet imprimiría «Descuento −1,98» a
      secas — justo el hueco que este campo existe para tapar, y encima solo en
      las ventas que nadie va a poder revisar hasta que sincronice.
    */
    discount_code_text: codigoDescuento ?? null,
    tax_total: q(totales.impuestoTotal),
    total: q(totales.total),
    amount_paid: q(recibido),
    change_total: q(vuelto),
    rounding_adjustment: q(ajuste),
    commission_total: "0.0000",

    note: nota,
    customer_id: clienteId,
    customer_name: clienteNombre,
    sold_by: vendedor.id,
    sold_by_name: vendedor.nombre,
    telefono_sucursal: opciones.telefonoSucursal ?? null,
    direccion_sucursal: opciones.direccionSucursal ?? null,

    created_at: ahora,
    synced_at: null,
    was_offline: true,

    voided_at: null,
    void_reason: null,

    items,
    payments: pagosSalida,
    was_duplicate: false,
  };
}

/**
 * Foto de las tasas para enviar con la venta.
 *
 * Es imprescindible: la venta se cobró con la tasa que el dispositivo tenía en
 * ese momento. Si no la enviáramos, el servidor recalcularía con la tasa de hoy
 * y el monto cobrado no cuadraría con el dinero que entró a la caja.
 */
export function fotoDeTasas(
  monedas: TenantCurrency[],
  monedaPrincipal: string,
): Record<string, string> {
  const tasas: Record<string, string> = {};

  for (const m of monedas) {
    if (m.currency_code === monedaPrincipal) continue;
    if (m.current_rate) tasas[m.currency_code] = m.current_rate;
  }

  return tasas;
}
